import Mustache from 'mustache';

class VehicleSearchQuicks {
  selectTargets = {};
  selectElements = {};
  mixedPairs = {};
  mixedTargets = {};
  templates = {};
  chiptionary = {};

  chips = {};
  showingMore = false;

  constructor(parent) {
    this.parent = parent;
    this.form = parent.vehicleSearchForm;

    this.handleQuickSelectChange = this.handleQuickSelectChange.bind(this);
    this.handleSideBarSelectChange = this.handleSideBarSelectChange.bind(this);
    this.handleQuickSelect2CheckChange = this.handleQuickSelect2CheckChange.bind(this);
    this.handleSideBarCheck2SelectChange = this.handleSideBarCheck2SelectChange.bind(this);
    this.updateChips = this.updateChips.bind(this);
    this.renderChips = this.renderChips.bind(this);
    this.chipRemoveHandler = this.chipRemoveHandler.bind(this);
    this.chipsToggleAll = this.chipsToggleAll.bind(this);

    this.initElements();
    this.initTemplates();
    this.initSelects();
    this.initMixedPairs();
    this.initChips();
  }

  /**
   * Initializes elements for quick search.
   */
  initElements() {
    this.form = document.querySelector('#vehicle-search-form');
    this.chipsContainer = document.querySelector('#vehicle-search-quicks-chips');
    this.chipsContainer.addEventListener('click', this.chipRemoveHandler);
    this.chipsInitialHeight = Math.max(32, this.chipsContainer.clientHeight);
    this.showMoreCounter = document.querySelector('#show-more-counter');

    this.selectElements = {
      quickMake: document.querySelector('#quick-make'),
      quickModel: document.querySelector('#quick-model'),
      quickFuel: document.querySelector('#quick-fuel'),
    };

    this.mixedPairs = {
      quickCategory: document.querySelector('#quick-search_type'),
    };

    this.chipsToggleAllButton = document.querySelector('#js-chips-toggle-all');
    this.chipsToggleAllButton.addEventListener('click', this.chipsToggleAll);
  }

  /**
   * Initializes templates for quick search.
   */
  initTemplates() {
    this.templates = {
      chips: document.querySelector('#mustache-chip').innerHTML,
    };
  }

  /**
   * Initializes event listeners for quick and sidebar filters.
   *
   * Attaches `change` event listeners to each quick filter and its corresponding
   * sidebar filter. When a change is detected in one, it triggers the synchronization process.
   */
  initSelects() {
    Object.entries(this.selectElements).forEach(([key, element]) => {
      const { filterTarget } = element.dataset;

      this.selectTargets[key] = document.getElementById(filterTarget);

      if (this.selectTargets[key]) {
        element.addEventListener('change', this.handleQuickSelectChange);
        this.selectTargets[key].addEventListener('change', this.handleSideBarSelectChange);
      }
    });
  }

  /**
   * Event handler for changes in the sidebar filter.
   * Synchronizes the corresponding quick filter to reflect the new selection in the sidebar.
   *
   * @param {Event} event - The change event triggered by the sidebar filter.
   */
  handleSideBarSelectChange(event) {
    const { target } = event;

    const multiselectTarget = window.r3multiselects[target.id];
    const multiselectSource = window.r3multiselects[`quick-${target.id}`];
    this.syncFilters(multiselectTarget, multiselectSource);
  }

  /**
   * Event handler for changes in the quick filter.
   * Synchronizes the corresponding sidebar filter to reflect the new selection in the quick filter.
   *
   * @param {Event} event - The change event triggered by the quick filter.
   */
  handleQuickSelectChange(event) {
    const { target } = event;

    const multiselectTarget = window.r3multiselects[target.id];
    const multiselectSource = window.r3multiselects[target.dataset.filterTarget];
    this.syncFilters(multiselectTarget, multiselectSource);
    this.form.dispatchEvent(new CustomEvent('change'));
  }

  /**
   * Initializes event listeners for mixed quick and sidebar filters.
   *
   * Attaches `change` event listeners to each quick filter and its corresponding
   * sidebar filter. When a change is detected in one, it triggers the synchronization process.
   */
  initMixedPairs() {
    Object.entries(this.mixedPairs).forEach(([key, element]) => {
      const { checkableTarget } = element.dataset;
      this.mixedTargets[checkableTarget] = document.getElementById(checkableTarget);

      if (this.mixedTargets[checkableTarget]) {
        element.addEventListener('change', this.handleQuickSelect2CheckChange);
        this.mixedTargets[checkableTarget].addEventListener('change', this.handleSideBarCheck2SelectChange);
      }
    });
  }

  /**
   * Event handler for changes in the sidebar filter.
   * Synchronizes the corresponding quick filter to reflect the new selection in the sidebar.
   *
   * @param {Event} event - The change event triggered by the sidebar filter.
   */
  handleSideBarCheck2SelectChange({ target }) {
    const parent = target.closest('.checkable__container');
    const multiselectTarget = window.r3multiselects[`quick-${parent.id}`];
    const values = [...parent.querySelectorAll(':checked')].map(o => o.value);
    multiselectTarget.value = values;
  }

  /**
   * Event handler for changes in the quick filter.
   * Synchronizes the corresponding sidebar filter to reflect the new selection in the quick filter.
   *
   * @param {Event} event - The change event triggered by the quick filter.
   */
  handleQuickSelect2CheckChange({ target }) {
    const { checkableTarget } = target.dataset;
    const values = [...target.selectedOptions].map(o => o.value).filter(Boolean);

    this.mixedTargets[checkableTarget].querySelectorAll('input').forEach(o => (o.checked = values.includes(o.value)));
    this.form.dispatchEvent(new CustomEvent('change'));
  }

  /**
   * Synchronizes the selections between the source filter and the target filter.
   * The target filter's selected options will be updated to match the source filter.
   *
   * @param {HTMLSelectElement} source - The filter element where the change occurred.
   * @param {HTMLSelectElement} target - The filter element to synchronize with the source.
   */
  syncFilters(source, target) {
    if (source && target) {
      target.value = source.value;
    }
  }

  /**
   * Updates chips.
   * @param {object[]} params - The currently selected filters.
   * @returns {number} number of selected filters
   */
  updateChips(params) {
    const validChips = params.filter(([key, value]) => value && !['order'].includes(key));
    const chipsRenderable = validChips
      .map(([key, value]) => {
        const chiptionaryEntry = this.chiptionary[`${key}:${value}`] ?? this.chiptionary[`${key}:range`];

        return {
          key,
          value,
          chipKey: key,
          chipValue: value,
          chipLabel: chiptionaryEntry?.label ?? value,
          chipPrefix: chiptionaryEntry?.groupLabel,
        };
      })
      .sort((a, b) => `${a.chipKey}:${a.chipValue}`.localeCompare(`${b.chipKey}:${b.chipValue}`));

    chipsRenderable.forEach(c => (c.showChipPrefix = Number.isFinite(parseFloat(c.chipLabel))));
    this.chips = chipsRenderable;
    this.renderChips();
    return this.chips.length;
  }

  /**
   * Updates chips content.
   * @param {object[]} chips - The currently selected filters.
   */
  renderChips(chips = this.chips) {
    this.chipsContainer.innerHTML = Mustache.render(this.templates.chips, { chips });
    this.updateShowMore(chips.length);
  }

  /**
   * ClickHandler to toggle wether more chips are being shown or not
   */
  chipsToggleAll() {
    this.showingMore = !this.showingMore;
    this.chipsContainer.classList.toggle('chip__container--more', this.showingMore);
  }

  /**
   * Chips container overflow detector
   *
   * @readonly
   * @returns {boolean} true if the chips container has one or more elements overflowing
   */
  get chipsIsOverflowing() {
    const overflowAmount = this.chipsContainer.scrollHeight - this.chipsInitialHeight;
    return overflowAmount > 5;
  }

  /**
   * Updates the button to detect if more need to be shown.
   * @param {number} chipsCount - number of chips.
   */
  updateShowMore(chipsCount) {
    this.chipsToggleAllButton.classList.toggle('hide', !this.chipsIsOverflowing);
    this.showMoreCounter.innerText = `(${chipsCount})`;
  }

  /**
   * Initialises the field to chip data mapping, aka chiptionary
   */
  initChips() {
    const selects = this.parent.vehicleSearchForm.querySelectorAll('select');
    selects.forEach(s => {
      const name = s.name;
      const options = s.options;
      const groupLabel = s.parentNode?.querySelector('label')?.innerText?.trim();

      [...options].forEach(o => {
        if (o.value) {
          this.chiptionary[`${name}:${o.value}`] = {
            element: name,
            value: o.value,
            label: o.innerText,
            groupLabel: groupLabel,
          };
        }
      });
    });

    const checkableInputs = this.parent.vehicleSearchForm.querySelectorAll('[type=checkbox],[type=radio]');
    checkableInputs.forEach(i => {
      const name = i.name;
      const label = i.closest('div')?.querySelector('label')?.innerText;
      let groupLabel = i.closest('.checkable__container')?.querySelector('.checkable__label')?.innerText?.trim();
      if (i.value) {
        this.chiptionary[`${name}:${i.value}`] = {
          element: name,
          value: i.value,
          label: label,
          groupLabel,
        };
      }
    });

    const rangeInputs = this.parent.vehicleSearchForm.querySelectorAll('[type=number]:not(#total_price_min):not(#total_price_max)');
    rangeInputs.forEach(i => {
      const name = i.name;
      const label = i.closest('div')?.querySelector('label')?.innerText?.replace(/\.$/, '').toLowerCase();
      let groupLabel = i.closest('fieldset')?.querySelector('legend')?.innerText?.trim();
      let value = 'range';
      if (i.value) {
        this.chiptionary[`${name}:${value}`] = {
          element: name,
          value: value,
          groupLabel: `${groupLabel} ${label}`,
        };
      }
    });

    const minmaxInputs = this.parent.vehicleSearchForm.querySelectorAll('#total_price_min,#total_price_max');
    minmaxInputs.forEach(i => {
      const name = i.name;
      const label = i.closest('label')?.innerText?.replace(/\.$/, '');
      let value = 'range';
      this.chiptionary[`${name}:${value}`] = {
        element: name,
        value: value,
        groupLabel: label,
      };
    });
  }

  /**
   * Click handler for removing a chip from the quick search
   *
   * @param {Event} {target} Click event object
   */
  chipRemoveHandler({ target }) {
    const button = target.closest('[data-chip-key]');
    if (button) {
      const { chipKey, chipValue } = button.dataset;
      const control = this.form.querySelector(`[name='${chipKey}']`);
      const option = control.querySelector(`[value='${chipValue}']`);
      const checkableInput = this.form.querySelector(`[name='${chipKey}'][value='${chipValue}']`);
      const input = this.form.querySelector(`[name='${chipKey}']`);

      switch (control.type) {
        case 'select-multiple':
        case 'select-one':
          option.removeAttribute('selected');
          option.selected = false;
          control.dispatchEvent(new Event('change'));
          break;
        case 'radio':
        case 'checkbox':
          checkableInput.removeAttribute('checked');
          checkableInput.checked = false;
          checkableInput.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        case 'number':
          if (input.dataset.blankValue) input.value = input.dataset.blankValue;
          else input.value = '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.form.dispatchEvent(new Event('change'));
    }
  }
}

export { VehicleSearchQuicks };
